import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PreviewParamType } from '@/lib/plugin-routing/preview-params';
import {
  detectResourceRequirements,
  getPrimaryParamType,
  type DetectedRequirements,
} from '@/lib/plugin-routing/resource-detection';
import { Monitor, Smartphone } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageSafe } from '../context/LanguageContext';
import { PreviewResourceManager } from './PreviewResourceManager';

// Preview page data structure (from backend)
interface PreviewPage {
  path: string;
  name?: string;
  fullUrl: string;
  features: any[];
  paramType: PreviewParamType;
  requirements?: {
    params?: Array<{
      name: string;
      type: 'query' | 'path' | 'context' | 'static';
      required?: boolean; // Defaults to true
    }>;
    resources?: string[];
  };
}

interface PreviewWithPageTabsProps {
  /** Preview pages with full URLs and requirements (from backend) */
  pages?: PreviewPage[];
  /** Deployment pages from instance (raw manifest data for accurate resource detection) */
  deploymentPages?: any[];
  /** Store ID for generating preview params */
  storeId: string;
  /** Instance ID for creating preview sessions */
  instanceId?: string;
  /** Iframe refresh key */
  iframeKey: number;
  /** Callback when selected page changes */
  onPageChange?: (pagePath: string) => void;
  /** Initial page path to select on mount (e.g. /thankyou) */
  initialPagePath?: string;
}

export interface PreviewWithPageTabsRef {
  applyStylesToElement: (elementId: string, styles: Record<string, string>) => void;
}

/**
 * Preview component with page tabs and param generation
 *
 * Displays a preview iframe with tabs for switching between different plugin pages.
 * Automatically handles parameter generation for pages that require it (checkout sessions, orders, etc.)
 *
 * 🎯 Enhanced Resource Detection:
 * - Uses shared resource detection utility (same as PreviewRequirementsDialog)
 * - Automatically detects required resources from plugin manifest
 * - Supports multiple resource types (product, variant, price, offer, order, checkoutSession)
 * - Falls back to backend paramType for backward compatibility
 */
export const PreviewWithPageTabs = forwardRef<PreviewWithPageTabsRef, PreviewWithPageTabsProps>(
  ({ pages = [], deploymentPages, storeId, instanceId, iframeKey, onPageChange, initialPagePath }, ref) => {
    const { t } = useTranslation('storefront');
    const [selectedPageIndex, setSelectedPageIndex] = useState(0);
    const initialPageApplied = useRef(false);
    const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
    const [previewParams, setPreviewParams] = useState<Record<string, string>>({});
    const [currentPreviewUrl, setCurrentPreviewUrl] = useState('');
    const [sessionPreviewUrl, setSessionPreviewUrl] = useState<string | undefined>(undefined);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Get selected language from context for locale query param
    const languageContext = useLanguageSafe();
    const selectedLanguage = languageContext?.selectedLanguage;

    // Auto-select initial page when pages load (e.g. navigating to thankyou page config)
    useEffect(() => {
      if (initialPageApplied.current || !initialPagePath || pages.length === 0) return;
      const normalizedTarget = initialPagePath.replace(/^\//, '');
      const idx = pages.findIndex(p => p.path.replace(/^\//, '') === normalizedTarget);
      if (idx >= 0) {
        setSelectedPageIndex(idx);
        initialPageApplied.current = true;
      }
    }, [pages, initialPagePath]);

    // Get the currently selected page
    const selectedPage = pages[selectedPageIndex];

    // Notify parent when page changes (including initial mount)
    useEffect(() => {
      if (selectedPage?.path && onPageChange) {
        onPageChange(selectedPage.path);
      } else if (!selectedPage?.path && onPageChange) {
        // Clear page path if no page is selected
        onPageChange('');
      }
    }, [selectedPageIndex, selectedPage?.path, onPageChange]);

    // Detect resource requirements from deployment pages (raw manifest data)
    // This ensures we use the actual deployment structure, not processed previewPages data
    const detectedRequirements: DetectedRequirements = useMemo(() => {
      // Prefer deploymentPages (raw manifest) over previewPages features
      if (deploymentPages && deploymentPages.length > 0 && selectedPage) {
        return detectResourceRequirements(deploymentPages, selectedPage.path);
      }

      // Fallback to previewPages features if deploymentPages not available
      if (selectedPage?.features) {
        const manifestPages = [
          {
            path: selectedPage.path,
            features: selectedPage.features,
          },
        ];
        return detectResourceRequirements(manifestPages, selectedPage.path);
      }

      return { resourceRequirements: [], manualRequirements: [], pathParamMap: {} };
    }, [deploymentPages, selectedPage]);

    // Get param type - use detected requirements if available, fallback to backend paramType
    const requiredParamType: PreviewParamType = useMemo(() => {
      const detectedType = getPrimaryParamType(detectedRequirements);
      // Use detected type if found, otherwise fallback to backend paramType
      return detectedType !== 'none' ? detectedType : selectedPage?.paramType || 'none';
    }, [detectedRequirements, selectedPage]);

    // Update preview URL when page or params change
    useEffect(() => {
      // If we have a session preview URL, ensure funnelTracking is disabled and add locale
      if (sessionPreviewUrl) {
        const url = new URL(sessionPreviewUrl);
        url.searchParams.set('funnelTracking', 'false'); // Always disable funnel tracking in preview
        // 🌍 Add locale from language selector
        if (selectedLanguage) {
          url.searchParams.set('locale', selectedLanguage);
        }
        setCurrentPreviewUrl(url.toString());
        return;
      }

      if (!selectedPage) {
        setCurrentPreviewUrl('');
        return;
      }

      // Start with the base URL from backend
      let fullUrl = selectedPage.fullUrl;

      // If params are provided, add them to the URL
      if (Object.keys(previewParams).length > 0) {
        // Handle path params (e.g., :orderId)
        Object.entries(previewParams).forEach(([key, value]) => {
          if (fullUrl.includes(`:${key}`)) {
            fullUrl = fullUrl.replace(`:${key}`, value);
          }
        });

        // Handle query params
        const url = new URL(fullUrl);
        Object.entries(previewParams).forEach(([key, value]) => {
          if (!selectedPage.fullUrl.includes(`:${key}`)) {
            url.searchParams.set(key, value);
          }
        });

        fullUrl = url.toString();
      }

      // 🎯 Add config editor preview params
      const finalUrl = new URL(fullUrl);
      finalUrl.searchParams.set('draft', 'true'); // Use draft data
      finalUrl.searchParams.set('forceReset', 'true'); // Hard refresh: clear cookies/localStorage
      finalUrl.searchParams.set('funnelTracking', 'false'); // Always disable funnel tracking in preview

      // 🌍 Add locale from language selector
      if (selectedLanguage) {
        finalUrl.searchParams.set('locale', selectedLanguage);
      }

      // 🌐 Add custom API base URL if VITE_LOCAL_TUNNEL_BASE_URL is defined
      // This allows using HTTPS tunnel for both plugin and API (solves mixed content issues)
      if (import.meta.env.VITE_LOCAL_TUNNEL_BASE_URL) {
        finalUrl.searchParams.set('tagadaClientBaseUrl', import.meta.env.VITE_LOCAL_TUNNEL_BASE_URL);
      }

      fullUrl = finalUrl.toString();

      setCurrentPreviewUrl(fullUrl);
    }, [selectedPage, previewParams, sessionPreviewUrl, selectedLanguage]);

    // Check if current page needs params and we don't have them yet
    const needsParams = requiredParamType !== 'none' && Object.keys(previewParams).length === 0;

    const handlePageChange = (index: number) => {
      setSelectedPageIndex(index);
      // Reset params and session URL when switching pages
      setPreviewParams({});
      setSessionPreviewUrl(undefined);
    };

    const handleResourceSelected = (params: Record<string, string>, previewUrl?: string) => {
      setPreviewParams(params);
      setSessionPreviewUrl(previewUrl);
    };

    // Check if all required resources are selected
    const areAllRequiredResourcesSelected = useMemo(() => {
      if (!detectedRequirements || detectedRequirements.resourceRequirements.length === 0) {
        // If no requirements detected, check if paramType needs params
        if (requiredParamType !== 'none') {
          return Object.keys(previewParams).length > 0;
        }
        return true; // No requirements, so it's ready
      }

      // Check all resource requirements
      const allRequirements = detectedRequirements.resourceRequirements;
      // Filter to only non-static required resources (static resources are saved to config and don't block preview)
      const requiredNonStaticResources = allRequirements.filter(
        (req) => !req.isStatic && req.required !== false,
      );

      if (requiredNonStaticResources.length === 0) {
        return true; // No required non-static resources
      }

      // Check if all required non-static resources have been selected
      return requiredNonStaticResources.every((req) => {
        const paramName = req.paramName || req.name || req.resourceName;
        return previewParams[paramName] !== undefined && previewParams[paramName] !== '';
      });
    }, [detectedRequirements, previewParams, requiredParamType]);

    const applyStylesToElement = (elementId: string, styles: Record<string, string>) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      // Send message to iframe to apply styles to element
      iframe.contentWindow.postMessage(
        {
          type: 'APPLY_STYLES_TO_ELEMENT',
          elementId,
          styles,
        },
        '*', // In production, you might want to specify the exact origin
      );
    };

    // Expose the function via ref
    useImperativeHandle(ref, () => ({
      applyStylesToElement,
    }));

    // Get display name for page - prefer name, then path, then feature type
    const getPageDisplayName = (page: PreviewPage): string => {
      // Prefer page name from manifest
      if (page.name) return page.name;
      // Fallback to path (formatted)
      if (page.path) return page.path;
      // Last resort: feature type
      if (page.features && page.features.length > 0) {
        const feature = page.features[0];
        if (typeof feature === 'string') return feature;
        return feature.type || 'Page';
      }
      return 'Page';
    };

    // If no pages, show message
    if (pages.length === 0) {
      return (
        <div className="flex h-full flex-col">
          <div className="bg-muted/20 flex flex-1 items-center justify-center">
            <p className="text-muted-foreground text-sm">{t('configEditor.preview.noPreviewPages')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col">
        {/* Combined bar: URL input, page selector, resource manager, and screen size switcher */}
        <PreviewResourceManager
          storeId={storeId}
          instanceId={instanceId}
          pagePath={selectedPage?.path || ''}
          pageFullUrl={selectedPage?.fullUrl}
          paramType={requiredParamType}
          detectedRequirements={detectedRequirements}
          onResourceSelected={handleResourceSelected}
          pageSelector={
            pages.length > 1 ? (
              <Select
                value={selectedPageIndex.toString()}
                onValueChange={(value) => handlePageChange(parseInt(value))}
              >
                <SelectTrigger className="h-8 w-[200px] text-xs font-medium shadow-sm">
                  <span className="truncate">{selectedPage ? getPageDisplayName(selectedPage) : 'Select page'}</span>
                </SelectTrigger>
                <SelectContent>
                  {pages.map((page, index) => {
                    const displayName = getPageDisplayName(page);
                    return (
                      <SelectItem key={index} value={index.toString()} className="text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="truncate">{displayName}</span>
                          {page.name && page.path && (
                            <span className="text-muted-foreground truncate text-[10px]">{page.path}</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : undefined
          }
          currentPreviewUrl={currentPreviewUrl}
          screenSizeSwitcher={
            <Tabs value={previewDevice} onValueChange={(v) => setPreviewDevice(v as 'desktop' | 'mobile')}>
              <TabsList className="bg-muted h-6 p-0.5">
                <TabsTrigger
                  value="desktop"
                  className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-5 px-2 data-[state=active]:shadow-sm"
                >
                  <Monitor className="h-3 w-3" />
                </TabsTrigger>
                <TabsTrigger
                  value="mobile"
                  className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-5 px-2 data-[state=active]:shadow-sm"
                >
                  <Smartphone className="h-3 w-3" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          }
        />

        {/* Preview iframe - only show when all required resources are selected */}
        <div className="bg-muted/20 relative flex-1 overflow-hidden">
          <div className="flex h-full items-center justify-center px-4 py-4">
            {areAllRequiredResourcesSelected && currentPreviewUrl ? (
              // Show iframe preview
              <iframe
                ref={iframeRef}
                data-preview-frame
                key={`${iframeKey}-${selectedPageIndex}`}
                src={currentPreviewUrl}
                className={`border-border bg-card h-full rounded-lg border-2 shadow-lg transition-all ${previewDevice === 'desktop' ? 'w-full' : 'w-[375px]'
                  }`}
                style={{ border: 'none' }}
                title={t('configEditor.preview.pluginPreview')}
              />
            ) : (
              // Show message when waiting for resource selection
              <div className="max-w-md space-y-3 text-center">
                <div className="bg-card border-border rounded-lg border-2 px-4 py-3 shadow-md">
                  <p className="text-muted-foreground text-sm font-medium">
                    {t('configEditor.preview.selectRequiredResources')}
                  </p>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {t('configEditor.preview.allResourcesRequired')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

PreviewWithPageTabs.displayName = 'PreviewWithPageTabs';
